/**
 * Buscador de clientes con creación rápida — usado al cobrar con "Fiado".
 * Muestra el saldo actual para que el cajero vea cuánto debe antes de
 * fiarle más (sin límite de crédito en v1).
 */

import { Button } from '@atreus/ui';
import { Plus, Search } from 'lucide-react';
import { useDeferredValue, useState } from 'react';

import { formatCOP } from '../../lib/format.js';
import { usePermissions } from '../../lib/permissions.js';
import { trpc } from '../../lib/trpc.js';

export interface PickedCustomer {
  id: string;
  fullName: string;
  balanceCents: number;
}

export const CustomerPicker = ({
  onSelect,
  onCancel,
}: {
  onSelect: (customer: PickedCustomer) => void;
  onCancel: () => void;
}) => {
  const utils = trpc.useUtils();
  const permissions = usePermissions();
  const canCreate = permissions.canDo('customers.manage') || permissions.canDo('payments.credit');

  const [search, setSearch] = useState('');
  const deferred = useDeferredValue(search.trim());
  const list = trpc.customers.list.useQuery(
    deferred ? { search: deferred, limit: 20 } : { limit: 20 },
  );

  const [creating, setCreating] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const create = trpc.customers.create.useMutation({
    onSuccess: (res) => {
      void utils.customers.list.invalidate();
      onSelect({ id: res.id, fullName: res.fullName, balanceCents: 0 });
    },
  });

  return (
    <div className="space-y-2 rounded-md border border-border bg-surface p-3">
      {creating ? (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({
              fullName,
              ...(phone.trim() ? { phone } : {}),
            });
          }}
        >
          <div className="text-sm font-semibold">Nuevo cliente</div>
          <input
            autoFocus
            aria-label="Nombre del cliente"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Nombre completo"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <input
            aria-label="Teléfono del cliente"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Teléfono (opcional)"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          {create.error ? <p className="text-xs text-danger">{create.error.message}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setCreating(false)}>
              Volver
            </Button>
            <Button type="submit" size="sm" disabled={!fullName.trim() || create.isPending}>
              {create.isPending ? 'Guardando…' : 'Guardar y usar'}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
            <input
              autoFocus
              type="search"
              aria-label="Buscar cliente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, teléfono o documento"
              className="w-full rounded-md border border-border bg-surface px-9 py-2 text-sm"
            />
          </div>
          <ul className="max-h-48 divide-y divide-border overflow-y-auto" aria-label="Clientes">
            {list.isLoading ? (
              <li className="py-2 text-xs text-fg-muted">Buscando…</li>
            ) : (list.data ?? []).length === 0 ? (
              <li className="py-2 text-xs text-fg-muted">Sin clientes para esta búsqueda.</li>
            ) : (
              (list.data ?? []).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() =>
                      onSelect({ id: c.id, fullName: c.fullName, balanceCents: c.balanceCents })
                    }
                    className="flex w-full items-center justify-between gap-2 px-1 py-2 text-left text-sm hover:bg-surface-muted"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">{c.fullName}</span>
                      {c.phone ? (
                        <span className="block text-xs text-fg-muted">{c.phone}</span>
                      ) : null}
                    </span>
                    {c.balanceCents > 0 ? (
                      <span className="shrink-0 text-xs font-bold text-danger">
                        Saldo {formatCOP(c.balanceCents)}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))
            )}
          </ul>
          <div className="flex justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancelar
            </Button>
            {canCreate ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFullName(search.trim());
                  setCreating(true);
                }}
              >
                <Plus className="size-3.5" /> Nuevo cliente
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};
